import { ChangeEvent, FormEvent, useState, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import User from "../../models/user.model";
import { Web3ContextType } from "../../providers/context.types";
import { Web3Context } from "../../providers/web3.context";
import { AppState } from "../../store/root.reducer";
import * as AuthActions from "../../pages/auth/store/auth.actions";
import { changeNameRequest } from "../../pages/auth/auth.api";
import { Form } from "react-bootstrap";
import { AlertComponent } from "../shared/alert.component";
import { TfaModal } from "./tfa.modal";
import { faEnvelope, faKey } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TfaType } from "../../pages/auth/store/auth.reducer";
import { ApiClient } from "../../utils/apiClient";
import { CustomModal } from "../templates/CustomModal";
import { SellerForm } from "../organisms/SellerForm";
import { axiosInstance } from "../../utils/api";
import { ToastContainer, toast } from "react-toastify";
import Button from "../molecules/Button";
import Heading from "../atoms/Heading";
import { useFTUE } from "../../contexts/FTUEContext";
import { FTUEStep } from "../../types/ftue.types";
import Select, { StylesConfig } from 'react-select';
import styles from './modal.module.css';
import {
  SketchfabAsset,
  SketchfabAssetOrderDirection,
  SketchfabCollection,
  SketchfabImportJobStatus,
  SketchfabOAuthStatus,
} from "../../types/sketchfabData";
import { consumeSketchfabOAuthCallback } from "./security.callback";
import { getNormalizedUserRoles, hasAdminCapability } from "../../utils/userAccess";

const BASE_API = process.env.REACT_APP_BASE_API;
const apiClient = ApiClient(BASE_API);

// TagOption interface for multiple select dropdown
export interface TagOption {
  readonly value: string;
  readonly label: string;
}

// Sample options for the multiple select dropdown
const externalSources = [
  { value: "europeana", label: "Europeana" },
];

const canUseSketchfabSrs = (user: User | null): boolean => {
  const roles = getNormalizedUserRoles(user);
  if (hasAdminCapability(user)) {
    return true;
  }

  return roles.includes("SELLER") && user?.curator === true && Boolean(user?.institutionId);
};

const EMPTY_SKETCHFAB_COUNTERS = {
  requested: 0,
  processed: 0,
  imported: 0,
  skippedDuplicates: 0,
  skippedNonDownloadable: 0,
  skippedLimitReached: 0,
  failed: 0,
};

const isSketchfabJobRunning = (job: SketchfabImportJobStatus | null) => job?.status === "running";

const formatSketchfabJobStatus = (status?: string) => {
  switch (status) {
    case "running":
      return "Importing";
    case "completed":
      return "Completed";
    case "completed_with_errors":
      return "Completed with errors";
    case "failed":
      return "Failed";
    default:
      return "Unknown";
  }
};

const formatSketchfabCreatedDate = (createdAt?: string): string | null => {
  if (!createdAt) {
    return null;
  }

  const parsed = Date.parse(createdAt);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return new Date(parsed).toISOString().slice(0, 10);
};

/**
 * Functions
 * 1. Change password
 * 2. Change wallet
 * 3. Set/Modify/Remove 2FA
 * @returns Security setting component
 */
type SecurityComponentProps = {
  showSketchfabSrs?: boolean;
};

export const SecurityComponent = ({ showSketchfabSrs = true }: SecurityComponentProps) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: AppState) => state.auth);
  const { startFTUE } = useFTUE();

  const user: User | null = auth.user;
  const userRoles = getNormalizedUserRoles(user);
  const isAdminSrsUser = hasAdminCapability(user);
  const canUseSrs = showSketchfabSrs && canUseSketchfabSrs(user);
  const displayInstitutionName = user?.institution?.name || (isAdminSrsUser ? "Artetra" : "");

  const tfaState: TfaType = auth.tfa;

  const { address } = useContext(Web3Context) as Web3ContextType;

  const [isChangePassword, setIsChangePassword] = useState<boolean>(false);
  const [isChange2FA, setIsChange2FA] = useState<boolean>(false);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [pendingName, setPendingName] = useState<string>(user?.name || "");
  const [displayName, setDisplayName] = useState<string>(user?.name || "");
  const [nameSaving, setNameSaving] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string>("");

  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");

  const [successAlert, setSuccessAlert] = useState<boolean>(false);
  const [errorAlert, setErrorAlert] = useState<boolean>(false);

  const [tfaModal, setTfaModal] = useState<boolean>(false);
  const [method, setMethod] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formServerErrors, setFormServerErrors] = useState<any>(null);
  const sellerFormRef = useRef<{ submitForm: () => boolean } | null>(null);
  
  // Multiple select dropdown state
  const [selectedExternalSources, setSelectedExternalSources] = useState<TagOption[]>([]);
  const [originalExternalSources, setOriginalExternalSources] = useState<TagOption[]>([]);
  const [sketchfabStatus, setSketchfabStatus] = useState<SketchfabOAuthStatus>({ connected: false });
  const [sketchfabCollections, setSketchfabCollections] = useState<SketchfabCollection[]>([]);
  const [sketchfabAssets, setSketchfabAssets] = useState<SketchfabAsset[]>([]);
  const [selectedSketchfabIds, setSelectedSketchfabIds] = useState<string[]>([]);
  const [sketchfabNextCursor, setSketchfabNextCursor] = useState<string | null>(null);
  const [sketchfabJob, setSketchfabJob] = useState<SketchfabImportJobStatus | null>(null);
  const [sketchfabLoading, setSketchfabLoading] = useState<boolean>(false);
  const [sketchfabActiveCollectionUid, setSketchfabActiveCollectionUid] = useState<string>("");
  const [sketchfabSearchDraft, setSketchfabSearchDraft] = useState<string>("");
  const [sketchfabActiveQuery, setSketchfabActiveQuery] = useState<string>("");
  const [sketchfabOrderDirection, setSketchfabOrderDirection] = useState<SketchfabAssetOrderDirection>("desc");
  const hasActiveSketchfabImport = isSketchfabJobRunning(sketchfabJob);
  const activeSketchfabCollection = sketchfabCollections.find(
    (collection) => collection.uid === sketchfabActiveCollectionUid
  ) || null;
  const sketchfabBrowseContextLabel = activeSketchfabCollection?.name || "All assets";
  const sketchfabSortLabel = sketchfabOrderDirection === "asc" ? "Oldest" : "Newest";

  const errorNotify = (msg: string) => {
    toast.error(msg, {
      position: 'bottom-left',
    })
  }

  const successNotify = (msg: string) => {
    toast.success(msg, {
      position: 'bottom-left',
    })
  }

  const trimmedPendingName = pendingName.trim();
  const isPendingNameValid = trimmedPendingName.length >= 2 && trimmedPendingName.length <= 80;
  const hasPendingNameChanged = trimmedPendingName !== (user?.name || "").trim();

  const beginNameEdit = () => {
    setPendingName(user?.name || "");
    setNameError("");
    setIsEditingName(true);
  };

  const cancelNameEdit = () => {
    setPendingName(user?.name || "");
    setNameError("");
    setIsEditingName(false);
  };

  const saveNameEdit = async () => {
    if (!isPendingNameValid || !hasPendingNameChanged) {
      return;
    }

    setNameSaving(true);
    setNameError("");
    const { data } = await changeNameRequest(trimmedPendingName);
    setNameSaving(false);

    if (data.status) {
      setDisplayName(data.user.name);
      setPendingName(data.user.name);
      setIsEditingName(false);
      dispatch(
        AuthActions.AuthenticationSuccess({
          user: data.user,
          msg: data.msg || "Name updated successfully",
        })
      );
      successNotify(data.msg || "Name updated successfully");
      return;
    }

    const nextError = data.response?.msg || data.msg || "Failed to update name";
    setNameError(nextError);
    errorNotify(nextError);
  };

  // Handler for multiple select dropdown
  const handleExternalSourcesChange = (value: TagOption[]) => {
    setSelectedExternalSources(value);
  };

  // Check if external sources have changed
  const hasExternalSourcesChanged = () => {
    if (selectedExternalSources.length !== originalExternalSources.length) {
      return true;
    }
    return selectedExternalSources.some(source => 
      !originalExternalSources.some(original => original.value === source.value)
    );
  };

  // Handler for saving external sources to backend
  const handleSaveExternalSources = async () => {
    try {
      const externalSourcesValues = selectedExternalSources.map(source => source.value);
      
      const response = await apiClient.saveExternalSources(externalSourcesValues);

      if (response.status !== false) {
        successNotify("External sources saved successfully!");
        
        // Update user in Redux with new external sources
        dispatch(AuthActions.UpdateExternalSourcesSuccess({
          user: {
            ...user,
            external_sources: externalSourcesValues
          } as User,
          msg: "External sources updated successfully"
        }));
        
        // Update original state to current state after successful save
        setOriginalExternalSources([...selectedExternalSources]);
      } else {
        errorNotify(response.data?.msg || "Failed to save external sources");
      }
    } catch (error: any) {
      console.error("Error saving external sources:", error);
      errorNotify("Failed to save external sources");
    }
  };

  const loadSketchfabStatus = async () => {
    if (!user || !canUseSrs) {
      return;
    }

    const response = await apiClient.getSketchfabOAuthStatus();
    if (response.status) {
      setSketchfabStatus(response.data);
      if (!response.data.connected) {
      setSketchfabCollections([]);
      setSketchfabAssets([]);
      setSelectedSketchfabIds([]);
      setSketchfabNextCursor(null);
      setSketchfabJob(null);
      setSketchfabActiveCollectionUid("");
      setSketchfabSearchDraft("");
      setSketchfabActiveQuery("");
      setSketchfabOrderDirection("desc");
      }
      return;
    }
  };

  const loadSketchfabCollections = async () => {
    if (!user || !canUseSrs || !sketchfabStatus.connected) {
      return;
    }

    const response = await apiClient.getSketchfabCollections();
    if (!response.status) {
      errorNotify(response.error?.message || "Failed to load Sketchfab collections");
      return;
    }

    const collections = response.data.collections || [];
    setSketchfabCollections(collections);
    if (sketchfabActiveCollectionUid && !collections.some((collection) => collection.uid === sketchfabActiveCollectionUid)) {
      setSketchfabActiveCollectionUid("");
    }
  };

  const loadLatestSketchfabJob = async () => {
    if (!user || !canUseSrs || !sketchfabStatus.connected) {
      return;
    }

    const response = await apiClient.getLatestSketchfabImportJob();
    if (response.status) {
      setSketchfabJob(response.data);
      return;
    }

    if (response.error?.code === "NOT_FOUND") {
      setSketchfabJob(null);
    }
  };

  const connectSketchfab = async () => {
    setSketchfabLoading(true);
    const response = await apiClient.getSketchfabOAuthStart();
    setSketchfabLoading(false);
    if (!response.status || !response.data?.authorizeUrl) {
      errorNotify(response.error?.message || "Failed to start Sketchfab OAuth");
      return;
    }

    window.location.href = response.data.authorizeUrl;
  };

  const disconnectSketchfab = async () => {
    setSketchfabLoading(true);
    const response = await apiClient.disconnectSketchfabOAuth();
    setSketchfabLoading(false);
    if (!response.status) {
      errorNotify(response.error?.message || "Failed to disconnect Sketchfab");
      return;
    }

    successNotify("Sketchfab disconnected");
    setSketchfabStatus({ connected: false });
    setSketchfabCollections([]);
    setSketchfabAssets([]);
    setSelectedSketchfabIds([]);
    setSketchfabNextCursor(null);
    setSketchfabJob(null);
    setSketchfabActiveCollectionUid("");
    setSketchfabSearchDraft("");
    setSketchfabActiveQuery("");
    setSketchfabOrderDirection("desc");
  };

  const resetSketchfabAssetSelection = () => {
    setSelectedSketchfabIds([]);
  };

  const loadSketchfabAssets = async (
    append: boolean = false,
    overrides?: {
      query?: string;
      orderDirection?: SketchfabAssetOrderDirection;
      collectionUid?: string;
    }
  ) => {
    if (!sketchfabStatus.connected) {
      return;
    }

    const hasQueryOverride = overrides ? Object.prototype.hasOwnProperty.call(overrides, "query") : false;
    const hasOrderOverride = overrides ? Object.prototype.hasOwnProperty.call(overrides, "orderDirection") : false;
    const hasCollectionOverride = overrides ? Object.prototype.hasOwnProperty.call(overrides, "collectionUid") : false;
    const query = hasQueryOverride ? overrides?.query || "" : sketchfabActiveQuery;
    const orderDirection = hasOrderOverride ? overrides?.orderDirection || "desc" : sketchfabOrderDirection;
    const collectionUid = hasCollectionOverride ? overrides?.collectionUid || "" : sketchfabActiveCollectionUid;
    setSketchfabLoading(true);
    const response = await apiClient.getSketchfabAssets(
      append ? sketchfabNextCursor || undefined : undefined,
      25,
      query,
      orderDirection,
      collectionUid || undefined
    );
    setSketchfabLoading(false);
    if (!response.status) {
      errorNotify(response.error?.message || "Failed to load Sketchfab assets");
      return;
    }

    const incoming = response.data.assets || [];
    setSketchfabAssets((current) => (append ? [...current, ...incoming] : incoming));
    setSketchfabNextCursor(response.data.nextCursor || null);
  };

  const submitSketchfabSearch = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const nextQuery = sketchfabSearchDraft.trim();
    setSketchfabActiveQuery(nextQuery);
    resetSketchfabAssetSelection();
    setSketchfabNextCursor(null);
    await loadSketchfabAssets(false, {
      query: nextQuery,
      orderDirection: sketchfabOrderDirection,
      collectionUid: sketchfabActiveCollectionUid || undefined,
    });
  };

  const clearSketchfabSearch = async () => {
    setSketchfabActiveCollectionUid("");
    setSketchfabSearchDraft("");
    setSketchfabActiveQuery("");
    setSketchfabOrderDirection("desc");
    resetSketchfabAssetSelection();
    setSketchfabNextCursor(null);
    await loadSketchfabAssets(false, {
      query: "",
      orderDirection: "desc",
      collectionUid: undefined,
    });
  };

  const handleSketchfabOrderChange = async (nextDirection: SketchfabAssetOrderDirection) => {
    if (nextDirection === sketchfabOrderDirection) {
      return;
    }
    setSketchfabOrderDirection(nextDirection);
    resetSketchfabAssetSelection();
    setSketchfabNextCursor(null);
    await loadSketchfabAssets(false, {
      query: sketchfabActiveQuery,
      orderDirection: nextDirection,
      collectionUid: sketchfabActiveCollectionUid || undefined,
    });
  };

  const handleSketchfabCollectionChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const nextCollectionUid = event.target.value || "";
    setSketchfabActiveCollectionUid(nextCollectionUid);
    resetSketchfabAssetSelection();
    setSketchfabNextCursor(null);
    await loadSketchfabAssets(false, {
      query: sketchfabActiveQuery,
      orderDirection: sketchfabOrderDirection,
      collectionUid: nextCollectionUid || undefined,
    });
  };

  const toggleSketchfabSelection = (sourceId: string) => {
    setSelectedSketchfabIds((current) =>
      current.includes(sourceId) ? current.filter((id) => id !== sourceId) : [...current, sourceId]
    );
  };

  const runSketchfabImport = async (mode: "selected" | "all" | "collection") => {
    if (mode === "selected" && selectedSketchfabIds.length === 0) {
      errorNotify("Select at least one Sketchfab asset");
      return;
    }

    if (mode === "collection" && !sketchfabActiveCollectionUid) {
      errorNotify("Select a Sketchfab collection");
      return;
    }

    const previousJob = sketchfabJob;
    setSketchfabLoading(true);
    setSketchfabJob((current) => current && current.status === "running" ? current : {
      jobId: "",
      status: "running",
      counters: { ...EMPTY_SKETCHFAB_COUNTERS },
      errorsSample: [],
      updatedAt: new Date().toISOString(),
    });
    const response = await apiClient.createSketchfabImportJob({
      mode,
      sourceIds: mode === "selected" ? selectedSketchfabIds : undefined,
      chunkSize: 25,
      cursor: mode === "all" ? undefined : undefined,
      collectionUid: mode === "collection" ? sketchfabActiveCollectionUid : undefined,
    });
    setSketchfabLoading(false);

    if (!response.status) {
      setSketchfabJob(previousJob);
      errorNotify(response.error?.message || "Import failed");
      if (response.error?.code === "IMPORT_LIMIT_EXCEEDED") {
        await loadSketchfabStatus();
      }
      return;
    }

    setSketchfabJob(response.data);
    if (response.data.jobId) {
      const refreshed = await apiClient.getSketchfabImportJob(response.data.jobId);
      if (refreshed.status) {
        setSketchfabJob(refreshed.data);
        successNotify(refreshed.data.status === "running" ? "Sketchfab import started" : "Sketchfab import job finished");
      } else {
        successNotify(response.data.status === "running" ? "Sketchfab import started" : "Sketchfab import job finished");
      }
    } else {
      successNotify(response.data.status === "running" ? "Sketchfab import started" : "Sketchfab import job finished");
    }
    setSelectedSketchfabIds([]);
    await loadSketchfabStatus();
  };

  // Styling for the multiple select dropdown
  const tagStyles: StylesConfig<TagOption, true> = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { isDisabled }) => {
      const color = "#000000";
      return {
        ...styles,
        color: color,
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: color,
        },
      };
    },
  };

  // Handle role change notifications
  useEffect(() => {
    if (!auth.loading && auth.msg && auth.msg.includes('Role')) {
      successNotify(auth.msg);
    }
    if (!auth.loading && auth.authError && typeof auth.authError === 'string' && auth.authError.includes('role')) {
      errorNotify(auth.authError);
    }
  }, [auth.loading, auth.msg, auth.authError]);

  const onChangeBuyerRole = () => {
    const activate = userRoles.includes("BUYER");
    dispatch(AuthActions.ChangeRole({ role: "BUYER", activate: activate ? 0 : 1 }));
  };

  const onChangePassword = () => {
    setIsChangePassword(!isChangePassword);
  };

  const onConfirmChangePassword = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccessAlert(true);
    setErrorAlert(true);
    dispatch(AuthActions.ChangePassword({ old: oldPassword, newPassword }));
  };

  const onEnable2FA = (method: string) => {
    setMethod(method);
    setModalFlags(true);
    dispatch(AuthActions.Change2FA(method));
  };

  const onRemove2FA = () => {
    setMethod("DISABLE");
    setModalFlags(true);
    dispatch(AuthActions.Change2FA("DISABLE"));
  };

  const setModalFlags = (flag: boolean) => {
    setTfaModal(flag);
    setSuccessAlert(flag);
    setErrorAlert(flag);
  };

  // Define FTUE steps for seller onboarding
  const startSellerOnboardingFTUE = () => {
    // Check if we're on mobile (screen width)
    const isMobile = window.innerWidth <= 991; // Bootstrap lg breakpoint
    
    const sellerSteps: FTUEStep[] = [
      {
        id: 'artworks-menu',
        title: isMobile ? 'Access Your Artworks' : 'Create your artworks',
        content: isMobile 
          ? 'Open the menu (☰) to find the Artworks section where you can upload and manage artworks to display on Artetra.'
          : 'Here you can upload new artworks you would like to display on Artetra.',
        targetSelector: isMobile ? '[data-ftue-step="header-hamburger-menu"]' : '[data-ftue-step="header-nav-artworks"]',
        position: isMobile ? 'left' : 'bottom'
      },
      {
        id: 'exhibitions-menu',
        title: isMobile ? 'Create Your Own Galleries' : 'Create Your Own Galleries',
        content: isMobile
          ? 'Open the menu (☰) to find My Galleries where you can create and edit your own galleries using artworks from your repository.'
          : 'In the My Galleries section, you can create and edit your own galleries.\nYou can then display the artworks from your Artworks repository in your galleries.',
        targetSelector: isMobile ? '[data-ftue-step="header-hamburger-menu"]' : '[data-ftue-step="header-nav-my-galleries"]',
        position: isMobile ? 'left' : 'bottom'
      }
    ];

    // Small delay to ensure the page and menu are fully rendered
    setTimeout(() => {
      startFTUE(sellerSteps);
    }, 1000);
  };

  // Define FTUE steps for profile page onboarding
  const startProfileFTUE = () => {
    // Check if we're on mobile (screen width)
    const isMobile = window.innerWidth <= 991; // Bootstrap lg breakpoint
    
    const profileSteps: FTUEStep[] = [
      {
        id: 'enable-exhibitor-profile',
        title: 'Enable your Exhibitor profile to display your atworks.',
        content: 'You can return here and enable the Exhibitor profile at any point by clicking on your profile name at the top right side of the screen.',
        targetSelector: '[data-ftue-step="enable-exhibitor-profile"]',
        position: 'right'
      },
      {
        id: 'exhibitions-menu',
        title: isMobile ? 'Open Menu to Find Exhibitions' : 'Visit Exhibitions',
        content: isMobile 
          ? 'Click the menu button (☰) to open the navigation menu where you can find the Exhibitions section to explore and create galleries.'
          : 'You are now ready to explore Artetra. Click on Exhibitions to begin.',
        targetSelector: isMobile ? '[data-ftue-step="header-hamburger-menu"]' : '[data-ftue-step="header-nav-exhibitions"]',
        position: isMobile ? 'left' : 'bottom'
      }
    ];

    // Small delay to ensure the page and menu are fully rendered
    setTimeout(() => {
      startFTUE(profileSteps);
    }, 500);
  };

  // Start profile FTUE when component mounts for non-seller users
  useEffect(() => {
    setDisplayName(user?.name || "");
  }, [user?.name]);

  useEffect(() => {
    if (!isEditingName) {
      setPendingName(user?.name || "");
    }
  }, [isEditingName, user?.name]);

  useEffect(() => {
    if (user && !userRoles.includes("SELLER")) {
      // Check if user has seen this FTUE before
      const hasSeenProfileFTUE = localStorage.getItem('hasSeenProfileFTUE');
      if (hasSeenProfileFTUE !== 'true') {
        startProfileFTUE();
        localStorage.setItem('hasSeenProfileFTUE', 'true');
      }
    }
  }, [user]);

  // Load external sources from user data on component mount
  useEffect(() => {
    if (user?.external_sources) {
      const sourceOptions = user.external_sources.map((source: string) => 
        externalSources.find(option => option.value === source)
      ).filter(Boolean) as TagOption[];
      
      setSelectedExternalSources(sourceOptions);
      setOriginalExternalSources([...sourceOptions]);
    } else {
      setSelectedExternalSources([]);
      setOriginalExternalSources([]);
    }
  }, [user]);

  useEffect(() => {
    const handledCallback = consumeSketchfabOAuthCallback({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    if (!handledCallback) {
      return;
    }

    if (handledCallback.status === "success") {
      successNotify("Sketchfab connected successfully");
    } else {
      errorNotify("Sketchfab connection failed");
    }

    window.history.replaceState(window.history.state, "", handledCallback.nextUrl);
  }, []);

  useEffect(() => {
    if (!user || !canUseSrs) {
      return;
    }

    loadSketchfabStatus();
  }, [canUseSrs, user]);

  useEffect(() => {
    if (!sketchfabStatus.connected) {
      return;
    }

    loadSketchfabCollections();
    // Collections and assets are independent initial loads for the connected panel.
    loadSketchfabAssets(false);
  }, [sketchfabStatus.connected]);

  useEffect(() => {
    if (!sketchfabStatus.connected) {
      setSketchfabJob(null);
      return;
    }

    loadLatestSketchfabJob();
  }, [sketchfabStatus.connected]);

  useEffect(() => {
    if (!sketchfabJob?.jobId || sketchfabJob.status !== "running") {
      return;
    }

    const intervalId = window.setInterval(async () => {
      const response = await apiClient.getSketchfabImportJob(sketchfabJob.jobId);
      if (response.status) {
        setSketchfabJob(response.data);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [sketchfabJob?.jobId, sketchfabJob?.status]);

  const handleSellerRole = () => {
    if (user && !userRoles.includes("SELLER")) {
      setFormServerErrors(null); // Clear previous errors when opening modal
      setIsModalOpen(true);
    }
    if (user && userRoles.includes("SELLER")) {
      dispatch(AuthActions.ChangeRole({ role: "SELLER", activate: 0 }));
    }
  };

  // Test function to simulate server errors (for debugging)
  const testFormErrors = () => {
    const testErrors = {
      institutionName: "Institution name is required",
      shortBio: "Bio must be at least 50 characters",
      websiteLink: "Invalid website URL format",
      facebookLink: "Facebook URL must start with https://facebook.com/",
      instagramLink: "Instagram handle not found"
    };
    console.log("Setting test errors:", testErrors);
    setFormServerErrors(testErrors);
  };
  const handleModalSubmit = async () => {
    if (sellerFormRef.current) {
      const formData = sellerFormRef.current.submitForm();

      if (
        formData &&
        typeof formData === "object" &&
        Object.keys(formData).length > 0
      ) {
        try {
          const response = await axiosInstance.post("users/verification-form", {
            ...(formData as Record<string, unknown>),
          });

          if (response.status === 201) {
            dispatch(AuthActions.ChangeRole({ role: "SELLER", activate: 1 }));
            setIsModalOpen(false);
            setFormServerErrors(null); // Clear errors on success
            
            // Start seller onboarding FTUE
            startSellerOnboardingFTUE();
          } else {
            console.error("Error submitting form:", response.data.msg);
            
            // Set server errors for the form to display field-specific errors
            if (response.data) {
              setFormServerErrors(response.data);
            }
            
            errorNotify(response.data.msg || "Error submitting form");
          }
        } catch (error: any) {
          setErrorAlert(true);
          
          // Set server errors for the form to display field-specific errors
          if (error.response?.data) {
            setFormServerErrors(error.response.data);
          }
          
          // Show general error notification
          errorNotify("Failed to submit form");
        }
      } else {
        errorNotify("Please fill all the required fields");
      }
    }
  };

  return (
    <>
      <div>
        {!auth.loading && auth.msg && successAlert ? (
          <AlertComponent
            onClose={setSuccessAlert}
            text={auth.msg as string}
            variant="success"
          />
        ) : (
          <></>
        )}

        {!auth.loading && auth.authError && errorAlert ? (
          <AlertComponent
            onClose={setErrorAlert}
            text={auth.authError}
            variant="danger"
          />
        ) : (
          <></>
        )}

        <div className="row mb-3">
          <div className="col-sm-12 col-md-6">
            Username{" "}
            {isEditingName ? (
              <input
                aria-label="Username"
                className="form-control mt-2"
                value={pendingName}
                onChange={(event) => setPendingName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    cancelNameEdit();
                  }
                }}
                autoFocus
              />
            ) : (
              <Heading h5 className="mb-0 mt-1">
                {displayName}
              </Heading>
            )}
            {nameError ? <div className="text-danger small mt-2">{nameError}</div> : null}
          </div>
          <div className="col-sm-12 col-md-5 d-flex flex-wrap gap-2 align-items-start mt-2 mt-md-0">
            {isEditingName ? (
              <>
                <button
                  aria-label="Save username"
                  className={`${styles["btn"]} ${styles["btn-primary"]}`}
                  onClick={saveNameEdit}
                  disabled={!isPendingNameValid || !hasPendingNameChanged || nameSaving}
                  type="button"
                >
                  Save
                </button>
                <button
                  aria-label="Cancel username"
                  className={styles["btn"]}
                  onClick={cancelNameEdit}
                  style={{ background: "#dc2626", color: "white" }}
                  type="button"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                aria-label="Change username"
                className={`${styles["btn"]} ${styles["btn-primary"]}`}
                onClick={beginNameEdit}
                type="button"
              >
                Change
              </button>
            )}
          </div>
        </div>

        <div className="row mb-3">
          {/* <div className="col-sm-12 col-md-6">
            Change wallet:{" "}
            {walletEdit ? (
              <>
                <em>{address}</em>
              </>
            ) : (
              <>{auth.user && auth.user.wallet}</>
            )}
          </div> */}
          {/* <div className="col-sm-12 col-md-5">
            <Button
              onClick={onChangeWallet}
              className="btn btn-success"
              disabled={walletEdit && address === auth.user.wallet}
            >
              {!walletEdit ? "Change" : "Confirm"}
            </Button>
          </div> */}
        </div>
        <div className="row mb-3">
          <div className="col-sm-12 col-md-6">Change password</div>
          <div className="col-sm-12 col-md-5">
            <button onClick={onChangePassword} className={`${styles['btn']} ${styles['btn-primary']}`}>
              {isChangePassword ? "Cancel" : "Change"}
            </button>
          </div>
        </div>
        {isChangePassword ? (
          <div className="row mb-2">
            <div className="col-md-6 col-sm-12">
              <Form onSubmit={onConfirmChangePassword}>
                <Form.Group className="mb-3" controlId="oldPassword">
                  <Form.Label>Old password</Form.Label>
                  <Form.Control
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    type="password"
                    placeholder="Enter old password"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>New password</Form.Label>
                  <Form.Control
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    type="password"
                    placeholder="Password"
                  />
                </Form.Group>
                <button
                  className={`${styles['btn']} ${styles['btn-primary']} mt-1`}
                  disabled={
                    (!oldPassword && !newPassword) ||
                    (auth.loading && isChangePassword)
                  }
                  type="submit"
                >
                  Confirm
                </button>
              </Form>
            </div>
          </div>
        ) : (
          <></>
        )}
        {/* <div className="row mb-3">
          <div className="col-sm-12 col-md-6">
            Two-Factor Authentication(2FA)
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <FontAwesomeIcon icon={faEnvelope} /> Email
          </div>
          <div className="col-md-5">
            {user && user.tfa.method === "EMAIL" ? (
              <Button
                className="btn btn-warning"
                onClick={onRemove2FA}
                disabled={auth.loading && isChange2FA}
              >
                Remove
              </Button>
            ) : (
              <Button
                className="btn btn-success"
                onClick={() => onEnable2FA("EMAIL")}
                disabled={auth.loading && isChange2FA}
              >
                Enable
              </Button>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <FontAwesomeIcon icon={faKey} /> Google Authenticator
          </div>
          <div className="col-md-5">
            {user && user.tfa.method === "GOOGLE" ? (
              <Button
                className="btn btn-warning"
                onClick={onRemove2FA}
                disabled={auth.loading && isChange2FA}
              >
                Remove
              </Button>
            ) : (
              <Button
                className="btn btn-success"
                onClick={() => onEnable2FA("GOOGLE")}
                disabled={auth.loading && isChange2FA}
              >
                Enable
              </Button>
            )}
          </div>
        </div> */}
        <div className="row mb-3">
          <div className="col-md-6">Enable Exhibitor Profile</div>
          <div className="col-md-5">
            <div data-ftue-step="enable-exhibitor-profile" style={{ display: 'inline-block' }}>
              <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={handleSellerRole}>
                {user && userRoles.includes("SELLER") ? "Disable" : "Enable"}
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">Enable Buyer Profile</div>
          <div className="col-md-5" title="Coming soon">
            <button 
              className={`${styles['btn']} ${styles['btn-secondary']}`} 
              onClick={onChangeBuyerRole}
              disabled={true}
              style={{ fontStyle: 'italic' }}
            >
              {user && userRoles.includes("BUYER") ? "Disable" : "Enable"}
            </button>
          </div>
        </div>

        {/* Multiple Select Dropdown for External Sources */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">External Sources</label>
            <Select 
              className="mb-1 basic-multi-select"
              classNamePrefix="select"
              defaultValue={[]}
              isMulti
              name="External Sources"
              options={externalSources}
              styles={{
                ...tagStyles,
                control: (styles, { isFocused }) => ({
                  ...styles,
                  backgroundColor: 'white',
                  borderRadius: '0.375rem',
                  minHeight: '38px',
                  border: isFocused ? '1px solid #86b7fe' : '1px solid #ced4da',
                  boxShadow: isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                  '&:hover': {
                    border: '1px solid #86b7fe'
                  }
                })
              }}
              onChange={handleExternalSourcesChange}
              value={selectedExternalSources}
              placeholder="Select external sources..."
            />
          </div>
          <div className="col-md-6 d-flex align-items-end">
            <button 
              className={`${styles['btn']} ${styles['btn-primary']} mt-3`}
              onClick={handleSaveExternalSources}
              disabled={!hasExternalSourcesChanged()}
            >
              Save Selection
            </button>
          </div>
        </div>

        {(displayInstitutionName || user?.institutionId || user?.curator) ? (
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Institution</label>
              <input className="form-control" value={displayInstitutionName || "Unavailable"} readOnly />
            </div>
          </div>
        ) : null}

        {canUseSrs && (
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Sketchfab OAuth & Import</label>
              <div className={styles.sketchfabPanel}>
                <div className={styles.sketchfabHeader}>
                  <div className={styles.sketchfabCard}>
                    <div className={styles.sketchfabEyebrow}>Account</div>
                    <div className={styles.sketchfabHeadline}>
                      {sketchfabStatus.connected
                        ? `Connected as ${sketchfabStatus.sketchfabUsername || "Sketchfab user"}`
                        : "Not connected"}
                    </div>
                    <div className={styles.sketchfabSummaryBlock}>
                      {isAdminSrsUser ? (
                        <div className={styles.sketchfabSummaryLine}>Import limit: Unlimited</div>
                      ) : (
                        <>
                          {user?.institution?.name ? (
                            <div className={styles.sketchfabSummaryLine}>{`Institution: ${user.institution.name}`}</div>
                          ) : null}
                          <div className={styles.sketchfabSummaryLine}>
                            {`Imported via SRS for your institution: ${sketchfabStatus.currentImportedCount || 0} / ${sketchfabStatus.effectiveImportLimit || 10}`}
                          </div>
                          <div className={styles.sketchfabSummaryHint}>This quota is shared across curator accounts in your institution.</div>
                          {sketchfabStatus.importLimit == null && (
                            <div className={styles.sketchfabSummaryHint}>Institution quota is not configured yet; using fallback 10.</div>
                          )}
                          {sketchfabStatus.connected && sketchfabStatus.quotaMode === "limited" && (
                            <div className={styles.sketchfabSummaryHint}>
                              {(sketchfabStatus.remainingImportSlots || 0) <= 0
                                ? "Your institution has reached its Sketchfab SRS import limit. Delete one of the institution's imported Sketchfab artworks to free a slot."
                                : `Your institution can import up to ${sketchfabStatus.remainingImportSlots || 0} more artworks right now.`}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {sketchfabStatus.connected ? (
                      <div className={styles.sketchfabBrowseContext}>
                        <div className={styles.sketchfabContextRow}>
                          <span>Browsing</span>
                          <strong>{sketchfabBrowseContextLabel}</strong>
                        </div>
                        <div className={styles.sketchfabContextRow}>
                          <span>Sort</span>
                          <strong>{sketchfabSortLabel}</strong>
                        </div>
                        {sketchfabActiveQuery ? (
                          <div className={styles.sketchfabContextRow}>
                            <span>Search</span>
                            <strong>{sketchfabActiveQuery}</strong>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        className={`${styles["btn"]} ${styles["btn-primary"]} ${styles.sketchfabConnectButton}`}
                        onClick={connectSketchfab}
                        disabled={sketchfabLoading}
                      >
                        Connect Sketchfab
                      </button>
                    )}
                  </div>

                  {sketchfabStatus.connected ? (
                    <form className={`${styles.sketchfabCard} ${styles.sketchfabControlsCard}`} onSubmit={submitSketchfabSearch}>
                      <div className={styles.sketchfabFieldGrid}>
                        <div className={styles.sketchfabField}>
                          <label className={`form-label ${styles.sketchfabFieldLabel}`} htmlFor="sketchfab-collection-select">
                            Collection
                          </label>
                          <select
                            id="sketchfab-collection-select"
                            className="form-select"
                            value={sketchfabActiveCollectionUid}
                            onChange={handleSketchfabCollectionChange}
                            disabled={sketchfabLoading || hasActiveSketchfabImport}
                          >
                            <option value="">All assets</option>
                            {sketchfabCollections.map((collection) => (
                              <option key={collection.uid} value={collection.uid}>
                                {collection.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.sketchfabField}>
                          <label className={`form-label ${styles.sketchfabFieldLabel}`} htmlFor="sketchfab-search-input">
                            Search
                          </label>
                          <input
                            id="sketchfab-search-input"
                            className="form-control"
                            type="text"
                            value={sketchfabSearchDraft}
                            onChange={(event) => setSketchfabSearchDraft(event.target.value)}
                            placeholder="Search by title"
                            disabled={sketchfabLoading || hasActiveSketchfabImport}
                          />
                        </div>
                      </div>

                      <div className={styles.sketchfabControlsFooter}>
                        <div className={styles.sketchfabField}>
                          <div className={`form-label ${styles.sketchfabFieldLabel}`}>Sort</div>
                          <div className={styles.sketchfabSortToggle} aria-label="Sort" role="group">
                            <button
                              type="button"
                              className={`${styles.sketchfabSortButton} ${sketchfabOrderDirection === "desc" ? styles.sketchfabSortButtonActive : ""}`}
                              aria-pressed={sketchfabOrderDirection === "desc"}
                              onClick={() => handleSketchfabOrderChange("desc")}
                              disabled={sketchfabLoading || hasActiveSketchfabImport}
                            >
                              Newest
                            </button>
                            <button
                              type="button"
                              className={`${styles.sketchfabSortButton} ${sketchfabOrderDirection === "asc" ? styles.sketchfabSortButtonActive : ""}`}
                              aria-pressed={sketchfabOrderDirection === "asc"}
                              onClick={() => handleSketchfabOrderChange("asc")}
                              disabled={sketchfabLoading || hasActiveSketchfabImport}
                            >
                              Oldest
                            </button>
                          </div>
                        </div>

                        <div className={styles.sketchfabFormActions}>
                          <button
                            type="submit"
                            className={`${styles["btn"]} ${styles["btn-primary"]}`}
                            disabled={sketchfabLoading || hasActiveSketchfabImport}
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className={`${styles["btn"]} ${styles["btn-secondary"]}`}
                            onClick={clearSketchfabSearch}
                            disabled={sketchfabLoading || hasActiveSketchfabImport}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </form>
                  ) : null}
                </div>

                {sketchfabStatus.connected ? (
                  <div className={styles.sketchfabActionBar}>
                    <button
                      className={`${styles["btn"]} ${styles["btn-secondary"]}`}
                      onClick={() => loadSketchfabAssets(false)}
                      disabled={sketchfabLoading || hasActiveSketchfabImport}
                    >
                      Refresh My Assets
                    </button>
                    <button
                      className={`${styles["btn"]} ${styles["btn-primary"]}`}
                      onClick={() => runSketchfabImport("selected")}
                      disabled={
                        sketchfabLoading ||
                        hasActiveSketchfabImport ||
                        selectedSketchfabIds.length === 0 ||
                        (sketchfabStatus.quotaMode === "limited" &&
                          (sketchfabStatus.remainingImportSlots || 0) <= 0)
                      }
                    >
                      Import Selected
                    </button>
                    <button
                      className={`${styles["btn"]} ${styles["btn-primary"]}`}
                      onClick={() => runSketchfabImport("collection")}
                      disabled={
                        sketchfabLoading ||
                        hasActiveSketchfabImport ||
                        !sketchfabActiveCollectionUid ||
                        (sketchfabStatus.quotaMode === "limited" &&
                          (sketchfabStatus.remainingImportSlots || 0) <= 0)
                      }
                    >
                      Import Selected Collection
                    </button>
                    <button
                      className={`${styles["btn"]} ${styles["btn-primary"]}`}
                      onClick={() => runSketchfabImport("all")}
                      disabled={
                        sketchfabLoading ||
                        hasActiveSketchfabImport ||
                        (sketchfabStatus.quotaMode === "limited" &&
                          (sketchfabStatus.remainingImportSlots || 0) <= 0)
                      }
                    >
                      Import All Downloadable
                    </button>
                    <button
                      className={`${styles["btn"]} ${styles["btn-secondary"]}`}
                      onClick={disconnectSketchfab}
                      disabled={sketchfabLoading || hasActiveSketchfabImport}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : null}
              </div>

              {sketchfabJob && (
                <div className={styles.sketchfabJobCard}>
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
                    <div>
                      <div className="text-white-50 small text-uppercase">Latest Import Job</div>
                      <div className="fw-semibold">{formatSketchfabJobStatus(sketchfabJob.status)}</div>
                      {sketchfabJob.collectionName || sketchfabJob.collectionUid ? (
                        <div className="small text-white-50">
                          {`Collection: ${sketchfabJob.collectionName || sketchfabJob.collectionUid}`}
                        </div>
                      ) : null}
                    </div>
                    {hasActiveSketchfabImport ? (
                      <div className="small text-warning">Import in progress. This can continue after you leave the page.</div>
                    ) : null}
                  </div>
                  <div className="row g-2 small">
                    <div className="col-sm-6">Requested: {sketchfabJob.counters?.requested || 0}</div>
                    <div className="col-sm-6">Processed: {sketchfabJob.counters?.processed || 0}</div>
                    <div className="col-sm-6">Imported: {sketchfabJob.counters?.imported || 0}</div>
                    <div className="col-sm-6">Duplicates: {sketchfabJob.counters?.skippedDuplicates || 0}</div>
                    <div className="col-sm-6">Non-downloadable: {sketchfabJob.counters?.skippedNonDownloadable || 0}</div>
                    <div className="col-sm-6">Limit reached: {sketchfabJob.counters?.skippedLimitReached || 0}</div>
                    <div className="col-sm-6">Failed: {sketchfabJob.counters?.failed || 0}</div>
                    {sketchfabJob.updatedAt ? (
                      <div className="col-sm-6">Updated: {new Date(sketchfabJob.updatedAt).toLocaleString()}</div>
                    ) : null}
                  </div>
                  {sketchfabJob.errorsSample?.length ? (
                    <div className="mt-3 small text-warning">
                      Sample error: {sketchfabJob.errorsSample[0].sourceId} - {sketchfabJob.errorsSample[0].message}
                    </div>
                  ) : null}
                </div>
              )}

              {sketchfabStatus.connected && (
                <div className={styles.sketchfabAssetList}>
                  {sketchfabAssets.length === 0 ? (
                    <div className={styles.sketchfabEmptyState}>
                      {sketchfabActiveQuery ? "No Sketchfab assets match this search." : "No assets loaded."}
                    </div>
                  ) : (
                    sketchfabAssets.map((asset) => (
                      <div key={asset.sourceId} className={styles.sketchfabAssetRow}>
                        <div className={styles.sketchfabAssetMeta}>
                          <input
                            type="checkbox"
                            checked={selectedSketchfabIds.includes(asset.sourceId)}
                            onChange={() => toggleSketchfabSelection(asset.sourceId)}
                            disabled={!asset.downloadable}
                          />
                          <div className={styles.sketchfabAssetText}>
                            <span>{asset.title}</span>
                            {formatSketchfabCreatedDate(asset.createdAt) && (
                              <small className="text-white-50">{`Created: ${formatSketchfabCreatedDate(asset.createdAt)}`}</small>
                            )}
                          </div>
                          {!asset.downloadable && <small className="text-white">(non-downloadable)</small>}
                        </div>
                        <small className={styles.sketchfabAssetLicense}>{asset.license}</small>
                      </div>
                    ))
                  )}
                </div>
              )}

              {sketchfabStatus.connected && sketchfabNextCursor && (
                <button
                  className={`${styles['btn']} ${styles['btn-secondary']} mt-2`}
                  onClick={() => loadSketchfabAssets(true)}
                  disabled={sketchfabLoading || hasActiveSketchfabImport}
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        )}

        {/* 2FA modal */}
        {tfaModal && tfaState.loading ? (
          <TfaModal
            method={method}
            show={tfaModal && tfaState.token !== null}
            onHide={() => setTfaModal(false)}
          />
        ) : (
          <></>
        )}
      </div>
      <CustomModal
        title="Verification Form"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleModalSubmit}
      >
        <SellerForm ref={sellerFormRef} serverErrors={formServerErrors} />
      </CustomModal>
      <ToastContainer />
    </>
  );
};
